import React, { useEffect, useState, useCallback } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  Clock, CheckCircle2, Play, XCircle, AlertOctagon, RefreshCw,
  Loader2, Zap, ArrowRight, Shield, Activity, X,
} from 'lucide-react';

// Action Center — Decision Orchestration UI.
// Architecture promise: NO optimistic UI. Every transition flows
//   UI action → backend transition request → state machine update
//   → audit event → refetch → UI sync
// Visual = Board (current state) + Timeline (audit chain in drawer).

const STATE_COLUMNS = [
  { key: 'pending',   label_ar: 'قيد المراجعة', label_en: 'Pending',   color: 'amber',   Icon: Clock },
  { key: 'approved',  label_ar: 'مُعتمَد',       label_en: 'Approved',  color: 'indigo',  Icon: Shield },
  { key: 'executed',  label_ar: 'مُنفَّذ',        label_en: 'Executed',  color: 'emerald', Icon: CheckCircle2 },
  { key: 'rejected',  label_ar: 'مرفوض',         label_en: 'Rejected',  color: 'gray',    Icon: XCircle },
  { key: 'failed',    label_ar: 'فشل',           label_en: 'Failed',    color: 'rose',    Icon: AlertOctagon },
];

const PRIORITY_TONE = {
  critical: 'border-l-rose-500',
  high:     'border-l-amber-500',
  medium:   'border-l-indigo-500',
  low:      'border-l-gray-400',
};

const fmtTime = (iso) => iso ? new Date(iso).toLocaleString() : '—';
const fmtSAR = (n) => n == null ? '—' : `${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })} SAR`;

// ─── Decision Card ──────────────────────────────────────────────────────
const DecisionCard = ({ decision, onOpen }) => {
  const tone = PRIORITY_TONE[decision.priority] || 'border-l-gray-300';
  return (
    <button
      type="button"
      data-testid={`dec-card-${decision.id}`}
      onClick={() => onOpen(decision)}
      className={`w-full text-left p-3 rounded-lg bg-white border border-gray-200 border-l-4 ${tone} hover:shadow-md transition-shadow`}
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="text-[10px] font-bold uppercase text-gray-500">{decision.priority || 'medium'}</span>
        <span className="text-[10px] text-gray-400 font-mono">{decision.anomaly_code}</span>
      </div>
      <p className="text-sm font-semibold text-gray-900 leading-snug">{decision.title_ar || decision.action}</p>
      <p className="text-xs text-gray-500 mt-1 font-mono">{decision.action}</p>
      {decision.expected_cash_impact_sar != null && (
        <p className="text-xs text-emerald-700 mt-1 font-semibold">~{fmtSAR(decision.expected_cash_impact_sar)}</p>
      )}
      <p className="text-[10px] text-gray-400 mt-1">{fmtTime(decision.proposed_at)}</p>
    </button>
  );
};

// ─── Detail Drawer + Timeline ──────────────────────────────────────────
const TimelineRow = ({ event }) => {
  const tone = {
    DECISION_PROPOSED: 'bg-amber-500',
    DECISION_APPROVED: 'bg-indigo-500',
    DECISION_EXECUTED: 'bg-emerald-500',
    DECISION_REJECTED: 'bg-gray-500',
    DECISION_FAILED:   'bg-rose-500',
  }[event.action] || 'bg-gray-400';
  return (
    <li className="flex gap-3">
      <div className="flex flex-col items-center">
        <span className={`w-3 h-3 rounded-full ${tone} mt-1.5`} />
        <span className="w-px flex-1 bg-gray-200" />
      </div>
      <div className="flex-1 pb-3">
        <p className="text-sm font-semibold text-gray-900">{event.action}</p>
        <p className="text-xs text-gray-500">{event.user_name || event.user_id} · {fmtTime(event.created_at)}</p>
        {event.metadata && (
          <pre className="text-[10px] text-gray-500 mt-1 bg-gray-50 rounded p-1 overflow-x-auto">
            {JSON.stringify(event.metadata, null, 0)}
          </pre>
        )}
      </div>
    </li>
  );
};

const Drawer = ({ decision, onClose, onTransition, busy }) => {
  const [timeline, setTimeline] = useState([]);
  const [tlLoading, setTlLoading] = useState(true);
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    if (!decision) return;
    setTlLoading(true);
    axiosInstance.get(`/cfo/decisions/${decision.id}/timeline`)
      .then(r => setTimeline(r.data.events || []))
      .catch(() => setTimeline([]))
      .finally(() => setTlLoading(false));
  }, [decision]);

  if (!decision) return null;
  const canApprove = decision.status === 'pending';
  const canExecute = decision.status === 'approved';
  const canReject  = decision.status === 'pending' || decision.status === 'approved';

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="action-drawer">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-md bg-white shadow-2xl overflow-y-auto">
        <div className="p-4 border-b border-gray-200 flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-[10px] font-mono text-gray-400">{decision.id}</p>
            <h3 className="font-bold text-gray-900 mt-0.5">{decision.title_ar}</h3>
            <p className="text-xs text-gray-500 mt-1">{decision.title_en}</p>
          </div>
          <button type="button" onClick={onClose} className="p-1 hover:bg-gray-100 rounded" data-testid="drawer-close">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          {/* Status + meta */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`px-2 py-0.5 rounded-full text-xs font-bold uppercase
              ${decision.status === 'pending'  ? 'bg-amber-100 text-amber-800' :
                decision.status === 'approved' ? 'bg-indigo-100 text-indigo-800' :
                decision.status === 'executed' ? 'bg-emerald-100 text-emerald-800' :
                decision.status === 'rejected' ? 'bg-gray-100 text-gray-800' :
                'bg-rose-100 text-rose-800'}`}>
              {decision.status}
            </span>
            <span className="text-xs text-gray-600">priority: <b>{decision.priority}</b></span>
            {decision.confidence != null && (
              <span className="text-xs text-gray-600">confidence: <b>{(decision.confidence * 100).toFixed(0)}%</b></span>
            )}
          </div>

          {/* Rationale */}
          <div className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3">{decision.rationale}</div>

          {/* Expected impact */}
          {decision.expected_impact && (
            <div>
              <p className="text-xs font-semibold text-gray-600 mb-1">Expected Impact</p>
              <div className="grid grid-cols-2 gap-1">
                {Object.entries(decision.expected_impact).map(([k, v]) => (
                  <div key={k} className={`text-xs px-2 py-1 rounded border ${v < 0 ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : v > 0 ? 'bg-rose-50 border-rose-200 text-rose-800' : 'bg-gray-50 border-gray-200 text-gray-600'}`}>
                    {k.replace('_risk', '')}: <b>{v > 0 ? '+' : ''}{v}</b>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Params + result */}
          {decision.params && Object.keys(decision.params).length > 0 && (
            <details className="text-xs">
              <summary className="cursor-pointer text-gray-600 font-semibold">Params</summary>
              <pre className="bg-gray-50 rounded p-2 mt-1 overflow-x-auto">{JSON.stringify(decision.params, null, 2)}</pre>
            </details>
          )}
          {decision.result && (
            <details className="text-xs" open>
              <summary className="cursor-pointer text-emerald-700 font-semibold">Result</summary>
              <pre className="bg-emerald-50 rounded p-2 mt-1 overflow-x-auto">{JSON.stringify(decision.result, null, 2)}</pre>
            </details>
          )}
          {decision.error && (
            <div className="text-xs text-rose-700 bg-rose-50 rounded p-2">
              <b>Error:</b> {decision.error}
            </div>
          )}

          {/* Transition controls — every action routes through backend */}
          {(canApprove || canExecute || canReject) && (
            <div className="pt-3 border-t border-gray-200 space-y-2">
              <p className="text-xs font-semibold text-gray-600">Transitions</p>
              {!rejectMode ? (
                <div className="flex flex-wrap gap-2">
                  {canApprove && (
                    <button
                      type="button"
                      data-testid="drawer-approve-btn"
                      disabled={busy}
                      onClick={() => onTransition('approve', { confirmation: true })}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50"
                    >
                      <Shield className="w-4 h-4" /> Approve
                    </button>
                  )}
                  {canExecute && (
                    <button
                      type="button"
                      data-testid="drawer-execute-btn"
                      disabled={busy}
                      onClick={() => onTransition('execute')}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50"
                    >
                      <Play className="w-4 h-4" /> Execute
                    </button>
                  )}
                  {canReject && (
                    <button
                      type="button"
                      data-testid="drawer-reject-btn"
                      disabled={busy}
                      onClick={() => setRejectMode(true)}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-lg border-2 border-rose-300 text-rose-700 text-sm font-semibold hover:bg-rose-50 disabled:opacity-50"
                    >
                      <XCircle className="w-4 h-4" /> Reject
                    </button>
                  )}
                  {busy && <Loader2 className="w-4 h-4 animate-spin text-gray-500" />}
                </div>
              ) : (
                <div className="space-y-2">
                  <input
                    type="text"
                    data-testid="reject-reason-input"
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Reason for rejecting…"
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:border-rose-500"
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={!rejectReason.trim() || busy}
                      onClick={() => onTransition('reject', { reason: rejectReason.trim() })}
                      className="flex-1 px-3 py-1.5 rounded-lg bg-rose-600 text-white text-sm font-semibold disabled:opacity-50"
                    >
                      Confirm Reject
                    </button>
                    <button
                      type="button"
                      onClick={() => { setRejectMode(false); setRejectReason(''); }}
                      className="px-3 py-1.5 rounded-lg border text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
              <p className="text-[10px] text-gray-400">
                Every transition validates server-side. UI reflects the new state after backend confirms — no optimistic updates.
              </p>
            </div>
          )}

          {/* Timeline */}
          <div className="pt-3 border-t border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <Activity className="w-4 h-4 text-gray-500" />
              <p className="text-xs font-semibold text-gray-600">Audit Timeline</p>
            </div>
            {tlLoading ? (
              <p className="text-xs text-gray-400">Loading…</p>
            ) : timeline.length === 0 ? (
              <p className="text-xs text-gray-400 italic">No audit events yet.</p>
            ) : (
              <ol className="space-y-0">
                {timeline.map((ev, i) => (
                  <TimelineRow key={i} event={ev} />
                ))}
              </ol>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main page ──────────────────────────────────────────────────────────
const ActionCenter = () => {
  const [decisions, setDecisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const r = await axiosInstance.get('/cfo/decisions?limit=200');
      setDecisions(r.data?.decisions || []);
    } catch (e) {
      toast.error('Failed to load decisions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Transition router — every UI action funnels through here so the
  // backend is the single source of truth.
  const onTransition = async (kind, payload = {}) => {
    if (!selected) return;
    setBusy(true);
    const url = `/cfo/decisions/${selected.id}/${kind}`;
    try {
      const r = await axiosInstance.post(url, payload);
      setSelected(r.data);
      toast.success(`Decision ${kind}d`);
      await fetchAll();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === 'object' ? `${detail.code}: ${detail.message}` : (detail || `Transition failed`);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const buckets = STATE_COLUMNS.map(col => ({
    ...col,
    items: decisions.filter(d => d.status === col.key),
  }));

  return (
    <div data-testid="action-center" className="space-y-6">
      <div className="page-header">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="page-title flex items-center gap-2">
              <Zap className="w-6 h-6 text-indigo-600" />
              Action Center · مركز القرارات
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Decision Orchestration UI — every transition flows UI → backend → state machine → audit → UI sync.
            </p>
          </div>
          <button
            type="button"
            onClick={fetchAll}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border-2 border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Board view — 5 state columns */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
        {buckets.map(col => {
          const Icon = col.Icon;
          return (
            <div key={col.key} data-testid={`col-${col.key}`} className={`rounded-xl border-2 border-${col.color}-200 bg-${col.color}-50/30 p-3`}>
              <div className="flex items-center justify-between mb-2">
                <div className={`flex items-center gap-2 text-${col.color}-800`}>
                  <Icon className="w-4 h-4" />
                  <span className="font-bold text-sm">{col.label_en}</span>
                </div>
                <span className={`text-xs font-bold bg-white border border-${col.color}-200 text-${col.color}-700 rounded-full px-2 py-0.5`}>
                  {col.items.length}
                </span>
              </div>
              <p className="text-[10px] text-gray-500 mb-2">{col.label_ar}</p>
              <div className="space-y-2 min-h-[60px]">
                {col.items.length === 0 ? (
                  <p className="text-xs text-gray-400 italic text-center py-4">No decisions</p>
                ) : col.items.slice(0, 20).map(d => (
                  <DecisionCard key={d.id} decision={d} onOpen={setSelected} />
                ))}
                {col.items.length > 20 && (
                  <p className="text-[10px] text-gray-400 text-center">+ {col.items.length - 20} more</p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-xs text-gray-400 text-center">
        Total decisions: {decisions.length} · UI never updates state optimistically — backend is the single source of truth.
      </p>

      {selected && (
        <Drawer
          decision={selected}
          onClose={() => setSelected(null)}
          onTransition={onTransition}
          busy={busy}
        />
      )}
    </div>
  );
};

export default ActionCenter;
